import AnnotationLink from "@pages/Article/components/ArticleParagraphElements/annotation/AnnotationLink";
import AnnotationStockPicker from "@pages/Article/components/ArticleParagraphElements/annotation/AnnotationStockPicker";
import { cn } from "@util/helpers";
import { DOMNode, domToReact, Element } from "html-react-parser";

export type AnnotationFactoryProps = {
  /** Callback function to execute when the annotation link is clicked */
  callback?: () => void;
  /** Optional CSS class to apply to the annotation elements */
  className?: string;
  /** Object containing prepend and append strings for paragraph annotations */
  appendObject?: {
    prepend?: string;
    append?: string;
  };
};

type TransformOptions = string | boolean | void | object | Element | null;

/**
 * Factory function to create an annotation transformer for HTML parsing.
 * It transforms specific HTML elements into React components.
 */
export const createAnnotation = ({
  callback,
  className,
  appendObject,
}: AnnotationFactoryProps) => {
  const transformStockPicker = (
    node: DOMNode
  ): string | boolean | void | object | Element | null => {
    if (!node || node.type !== "tag") return null;

    if (node.name === "a") {
      const el = node;
      const children = el.children as DOMNode[];

      return <>{domToReact(children, { replace: transform })}</>;
    }
  };

  const transform = (node: DOMNode): TransformOptions => {
    if (!node || node.type !== "tag") return null;

    const attributes = node.attribs || {};
    const splitClass = attributes.class?.split(" ") || [];

    const isStockPicker = splitClass.includes("sgxstock");

    if (node.name === "span" && isStockPicker) {
      const el = node;
      const children = el.children as DOMNode[];
      const stockCode = el.attribs["data-stockcode"];

      return (
        <AnnotationStockPicker
          stockCode={stockCode}
          className={cn("sgxstock", className)}
        >
          {domToReact(children, { replace: transformStockPicker })}
        </AnnotationStockPicker>
      );
    }

    if (node.name === "a") {
      const el = node;

      const children = el.children as DOMNode[];
      const href = el.attribs.href;
      return (
        <AnnotationLink href={href} callback={callback} attributes={el.attribs}>
          {domToReact(children, { replace: transform })}
        </AnnotationLink>
      );
    }

    if (node.name === "p") {
      const el = node;
      const children = el.children as DOMNode[];

      return (
        <p className={cn("mb-6 whitespace-pre-wrap", className)}>
          {appendObject?.prepend ? <span>{appendObject.prepend}</span> : null}

          {domToReact(children, {
            replace: (childNode: DOMNode) => {
              if (childNode.type === "tag" && childNode.name === "a") {
                return transform(childNode);
              }

              return childNode;
            },
          })}

          {appendObject?.append ? <span>{appendObject.append}</span> : null}
        </p>
      );
    }

    return null;
  };

  return transform;
};
